import { useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@utils/consts';
import { DefaultApiResp, InventoryLocation } from '@root/globalTypes';
import { queryFetch } from '@services/api';
import { useQueryParams } from '@hooks/queryParams';
import { CreateInventoryRoutePointsPairReq } from './types';
import { useErrors } from '@hooks/errors';

export const useCreateInventoryRoutePointsPair = ({
  location,
  callback,
}: {
  location: InventoryLocation;
  callback: (data: DefaultApiResp) => void;
}) => {
  const {
    params: { routeId },
    parseParams,
  } = useQueryParams();

  const queryClient = useQueryClient();
  const handleErrors = useErrors({ allInToast: true });

  const queryParams = parseParams({ routeId, ...location });

  const { mutate, isPending } = useMutation({
    mutationFn: (data: CreateInventoryRoutePointsPairReq): Promise<DefaultApiResp> =>
      queryFetch({
        url: 'routes/points/create_pair',
        data: { pairPoints: { ...data, routeId } },
        opt: { method: 'POST' },
      }),
    onError: (err) => {
      console.error(err);
    },
    onSuccess: async ({ success }) => {
      if (!success) return;
      await queryClient.refetchQueries({
        queryKey: [...queryKeys.inventoryAvailable, queryParams],
      });
      await queryClient.refetchQueries({
        queryKey: [...queryKeys.inventoryRequested, queryParams],
      });
      await queryClient.refetchQueries({ queryKey: [...queryKeys.inventoryRoutes, routeId] });
      await queryClient.refetchQueries({ queryKey: queryKeys.dropdowns.routes });
    },
    onSettled: (data) => {
      if (!data) return;
      const { success, errors } = data as DefaultApiResp;
      if (errors) handleErrors(errors);
      if (success) callback(data as DefaultApiResp);
    },
  });

  return {
    mutate,
    isLoading: isPending,
  };
};
